import { FC, useEffect } from 'react';
import { Menu, MenuItem, ListItemIcon, Typography, Link, LinearProgress } from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { Logout, SpaceDashboard } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from 'react-query';
import { datadogLogs } from '@datadog/browser-logs';
import useGentian from '../../core/hooks/useGentian';
import { isReseller } from '../../Utils/account.utils';

export interface AvatarMenuProps {
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
}

export const AvatarMenuComponent: FC<AvatarMenuProps> = ({ anchorEl, open, onClose }) => {
  const queryClient = useQueryClient();
  const { logout } = useAuth0();

  const { useAccount, useCreditByUserId } = useGentian();
  const { data: account } = useAccount();
  const userId = account?.data?.user_id as string | undefined;

  const { data: creditData, isLoading: creditLoading, refetch: refetchCredit } = useCreditByUserId(userId);
  const reseller = isReseller(account);

  useEffect(() => {
    if (open && userId) {
      refetchCredit();
    }
  }, [open, userId, refetchCredit]);

  const handleClose = () => {
    onClose();
  };

  const handleLogout = async () => {
    try {
      datadogLogs.logger.info('User decided to log out...');
      datadogLogs.logger.info('Cancelling all queries and mutations...');
      await queryClient.cancelQueries();
      await queryClient.cancelMutations();
      datadogLogs.logger.info('Logging out...');

      await logout({ logoutParams: { returnTo: window.location.origin } });

      datadogLogs.logger.info('Removing auth token from local storage...');
    } catch (error) {
      datadogLogs.logger.error('Error during logout process', { error });
    }
  };

  return (
    <Menu
      id="account-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      keepMounted
      disableScrollLock
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
            '.MuiMenu-list': {
              width: 200,
            },
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      {/* <MenuItem onClick={() => handleMenuClick(MenuClickTarget.PROFILE)}>
        <ListItemIcon>
          <AccountCircle fontSize="small" />
        </ListItemIcon>
        Profile
      </MenuItem>
      <Divider />
      <MenuItem onClick={() => handleMenuClick(MenuClickTarget.SETTINGS)}>
        <ListItemIcon>
          <Settings fontSize="small" />
        </ListItemIcon>
        Settings
      </MenuItem> */}
      {reseller && (
        <MenuItem
          disabled
          sx={{
            cursor: 'default',
            opacity: 1,
            '&.Mui-disabled': { opacity: 1 },
            p: 0,
            mb: 1.5,
          }}
        >
          <div
            style={{
              width: '240px',
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 12,
              border: '1px solid #e6e6e6',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                mb: 1,
                color: '#444',
              }}
            >
              Credit Summary
            </Typography>

            {creditLoading ? (
              <Typography variant="body2">Loading...</Typography>
            ) : creditData ? (
              <>
                {/* PROGRESS BAR */}
                <LinearProgress
                  variant="determinate"
                  value={(creditData.amountUsed / creditData.debtLimit) * 100}
                  sx={{
                    width: '100%',
                    height: 8,
                    borderRadius: 5,
                    mb: 1.5,
                    backgroundColor: '#e9e9e9',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      backgroundColor:
                        (creditData.amountUsed / creditData.debtLimit) * 100 < 60
                          ? '#4caf50' // green
                          : (creditData.amountUsed / creditData.debtLimit) * 100 < 85
                            ? '#ff9800' // yellow
                            : '#f44336', // red
                    },
                  }}
                />

                {/* NUMBERS */}
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Used:</strong> £{creditData.amountUsed.toLocaleString()}
                </Typography>

                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Remaining:</strong> £
                  {Math.max(creditData.debtLimit - creditData.amountUsed, 0).toLocaleString()}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Limit: £{creditData.debtLimit.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Total Spend: £{creditData.totalSpend.toLocaleString()}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">No credit info available.</Typography>
            )}
          </div>
        </MenuItem>
      )}

      <MenuItem>
        <ListItemIcon>
          <SpaceDashboard fontSize="small" />
        </ListItemIcon>
        <Link href="/mydashboard" color="inherit">
          <Typography variant="body2" color="text.primary">
            My Dashboard
          </Typography>
        </Link>
      </MenuItem>
      {reseller && (
        <MenuItem>
          <ListItemIcon>
            <ManageAccountsIcon fontSize="small" />
          </ListItemIcon>
          <Link href="/accounts" color="inherit">
            <Typography variant="body2" color="text.primary">
              Accounts
            </Typography>
          </Link>
        </MenuItem>
      )}

      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <Logout fontSize="small" />
        </ListItemIcon>
        <Typography variant="body2" color="text.primary">
          Logout
        </Typography>
      </MenuItem>
      {/* <MenuItem onClick={() => {}}>
        <ListItemIcon>
          <Wallet fontSize="small" />
        </ListItemIcon>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="body1">Wallet</Typography>
        </Box>
      </MenuItem>
      <MenuItem
        sx={{
          '&:hover': {
            backgroundColor: 'unset !important',
            cursor: 'auto',
          },
        }}
      >
        Balance
      </MenuItem>
      <MenuItem
        sx={{
          justifyContent: 'center',
          '&:hover': {
            backgroundColor: 'unset !important',
            cursor: 'auto',
          },
        }}
      >
        <GentianToken /> 0 Gentian Tokens
      </MenuItem>
      <MenuItem
        sx={{
          justifyContent: 'center',
          '&:hover': {
            backgroundColor: 'unset !important',
            cursor: 'auto',
          },
        }}
      >
        <Box>
          <Link href="/pricing/token_info">
            <Button variant="contained" color="primary" size="small">
              TOP UP
            </Button>
          </Link>
        </Box>
      </MenuItem> */}
    </Menu>
  );
};
